import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Modal,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  LinearProgress,
  alpha,
} from "@mui/material";
import {
  Close,
  AttachMoney,
  CalendarToday,
  Settings,
  ExpandMore,
  Clear,
  CheckCircle,
  Block,
  Warning,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { BackdropComponent } from "../../../shared-components/backdrop/Backdrop";
import { createDebits } from "../../../services/request/Debits";
import { getBorrowerById } from "../../../services/request/Borrowers";
import { BorrowerAPIItem } from "../../../models/LendingModels";
import { useTheme } from "@mui/material/styles";
import AmountInput from "../../../shared-components/amount-input/AmountInput";

const durationTypes = ["Days", "Weeks", "Months", "Years"];
const statuses = ["PENDING", "ACTIVE", "CLOSED", "DEFAULTED"];
const Type = ["INTEREST", "SHORT_TERM_DEBIT"];

interface DebitCaptureModalProps {
  open: boolean;
  borrowerId: string;
  borrower?: BorrowerAPIItem;
  handleDebitModelClose: () => void;
  onDebitCreated?: () => void;
}

const DebitCaptureModal: React.FC<DebitCaptureModalProps> = ({
  open,
  borrowerId,
  borrower,
  handleDebitModelClose,
  onDebitCreated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loader, setLoader] = React.useState(false);
  const { leadId = "" } = useParams();

  const [borrowerInfo, setBorrowerInfo] = useState<BorrowerAPIItem | null>(borrower || null);
  const [loadingBorrowerCheck, setLoadingBorrowerCheck] = useState(false);
  const [outstandingManuallyEdited, setOutstandingManuallyEdited] = useState(false);

  const [formData, setFormData] = useState({
    amount: '',
    type: "INTEREST",
    outstanding: '',
    interestRate: '',
    durationType: "Months",
    startDate: new Date().toISOString().substring(0, 10),
    endDate: "",
    emiAmount: '',
    paymentDate: "5",
    status: "ACTIVE",
  });

  // Fetch / verify borrower details and blocklist status whenever modal opens
  useEffect(() => {
    if (open) {
      setOutstandingManuallyEdited(false);
      if (borrower) {
        setBorrowerInfo(borrower);
      }
      if (borrowerId) {
        setLoadingBorrowerCheck(true);
        getBorrowerById(borrowerId)
          .then((res: any) => {
            if (res && res.id) {
              setBorrowerInfo(res as BorrowerAPIItem);
            }
          })
          .catch((err) => {
            console.error("Error checking borrower blocklist status:", err);
          })
          .finally(() => {
            setLoadingBorrowerCheck(false);
          });
      }
    }
  }, [open, borrowerId, borrower]);

  // Determine if borrower is in the block list / blacklisted
  const isCustomerBlocked = useMemo(() => {
    if (!borrowerInfo) return false;
    const statusBlocked =
      borrowerInfo.creditStatus?.toUpperCase() === 'BLOCKED' ||
      borrowerInfo.creditStatus?.toUpperCase() === 'BLACKLISTED';
    const commentsBlocked = Boolean(
      borrowerInfo.comments && borrowerInfo.comments.toLowerCase().includes('blacklist')
    );
    return statusBlocked || commentsBlocked;
  }, [borrowerInfo]);

  // Auto-populate outstanding amount from principal amount by default, while allowing user to edit
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setFormData(prev => {
        const shouldSync = !outstandingManuallyEdited || prev.outstanding === '' || prev.outstanding === prev.amount;
        return {
          ...prev,
          amount: value,
          outstanding: shouldSync ? value : prev.outstanding,
        };
      });
    } else if (name === 'outstanding') {
      setOutstandingManuallyEdited(true);
      setFormData(prev => ({ ...prev, outstanding: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, [outstandingManuallyEdited]);

  const handleClear = () => {
    setOutstandingManuallyEdited(false);
    setFormData({
      amount: '',
      type: "INTEREST",
      outstanding: '',
      interestRate: '',
      durationType: "Months",
      startDate: new Date().toISOString().substring(0, 10),
      endDate: "",
      emiAmount: '',
      paymentDate: "5",
      status: "ACTIVE",
    });
  };

  const handleSubmit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid Principal Amount');
      return;
    }

    setLoader(true);
    try {
      const payload = {
        ...formData,
        // Ensure outstanding defaults to amount if left blank
        outstanding: formData.outstanding !== '' ? formData.outstanding : formData.amount,
      };

      createDebits(borrowerId, payload)
        .then((result) => {
          setLoader(false);
          if (result.status === 200 && result.data.loanId) {
            toast.success("Outstanding Amount Added Successfully! 🎉");
            if (onDebitCreated) {
              onDebitCreated();
            }
            handleDebitModelClose();
          } else if (result.status === 403) {
            toast.error("Invalid Login - Please check your credentials", { 
              position: "bottom-center",
              autoClose: 5000
            });
          } else {
            toast.error(`Failed to create outstanding: ${result.statusText || 'Unknown error'}`, {
              position: "bottom-center",
              autoClose: 5000
            });
          }
        })
        .catch((err) => {
          console.error("API Error:", err);
          setLoader(false);
          
          if (err.response) {
            const { status, data } = err.response;
            switch (status) {
              case 400:
                let errorMessage = "Invalid data provided";
                if (data && data.message) errorMessage = data.message;
                else if (data && data.error) errorMessage = data.error;
                toast.error(`❌ ${errorMessage}`, { position: "top-right", autoClose: 7000 });
                break;
              case 401:
                toast.error("🔐 Unauthorized - Please login again", { position: "top-right", autoClose: 5000 });
                break;
              case 403:
                toast.error("🚫 Access Denied - Insufficient permissions", { position: "top-right", autoClose: 5000 });
                break;
              case 404:
                toast.error("🔍 Customer not found", { position: "top-right", autoClose: 5000 });
                break;
              case 422:
                toast.error("📝 Validation failed - Please check your inputs", { position: "top-right", autoClose: 5000 });
                break;
              case 500:
                toast.error("🔧 Server error - Please try again later", { position: "top-right", autoClose: 5000 });
                break;
              default:
                toast.error(`⚠️ Failed to create outstanding (Error ${status})`, { position: "top-right", autoClose: 5000 });
            }
          } else if (err.request) {
            toast.error("🌐 Network error - Please check your connection", { position: "top-right", autoClose: 5000 });
          } else {
            toast.error("❌ An unexpected error occurred", { position: "top-right", autoClose: 5000 });
          }
        });
    } catch (err) {
      console.error("Unexpected Error:", err);
      setLoader(false);
      toast.error("❌ An unexpected error occurred while processing your request", {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const amountFields = [
    { name: "amount", label: "Principal Amount" },
    { name: "outstanding", label: "Outstanding Amount (Auto-filled)" },
    { name: "interestRate", label: "Interest Rate (%)" },
    { name: "emiAmount", label: "EMI Amount" },
  ];

  const dateFields = [
    { name: "startDate", label: "Start Date" },
    { name: "endDate", label: "End Date" },
    { name: "paymentDate", label: "Payment Date" },
  ];

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "95vw" : "620px",
    maxWidth: "95vw",
    maxHeight: "92vh",
    bgcolor: "background.paper",
    borderRadius: 3,
    boxShadow: 24,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const FormContent = useMemo(() => (
    <Box sx={{ flexGrow: 1, overflow: "auto", p: 0 }}>
      <Stack spacing={2}>
        {/* Top Block List / Blacklist Verification Section */}
        {loadingBorrowerCheck && !borrowerInfo ? (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', border: '1px dashed', borderColor: 'divider' }}>
            <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Verifying customer block list and credit standing…
            </Typography>
          </Box>
        ) : borrowerInfo ? (
          isCustomerBlocked ? (
            <Alert
              severity="error"
              icon={<Block sx={{ fontSize: 24, color: 'error.main' }} />}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.main',
                bgcolor: alpha(theme.palette.error.main, 0.08),
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'error.dark' }}>
                    ⚠️ Block List Check: Customer is Blocked / Blacklisted
                  </Typography>
                  <Chip
                    label={borrowerInfo.creditStatus || "BLOCKED"}
                    size="small"
                    color="error"
                    sx={{ fontWeight: 800, height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                  Customer: <strong>{borrowerInfo.name}</strong> ({borrowerInfo.borrowerId || borrowerInfo.id.substring(0, 8)})
                  {borrowerInfo.creditScore != null && ` · Credit Score: ${borrowerInfo.creditScore}`}
                </Typography>
                {borrowerInfo.comments && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      p: 0.75,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px dashed',
                      borderColor: 'error.light',
                      color: 'text.secondary',
                    }}
                  >
                    <strong>Reason / Note:</strong> {borrowerInfo.comments}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.3,
                    color: 'warning.dark',
                    fontWeight: 700,
                  }}
                >
                  ℹ️ Notice: Submission is NOT blocked. You can still proceed with creating this debit record.
                </Typography>
              </Box>
            </Alert>
          ) : (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: '1px solid',
                borderColor: alpha(theme.palette.success.main, 0.3),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" sx={{ fontSize: 22 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark', fontSize: '0.84rem' }}>
                    Block List Check: Clear (Not Blacklisted)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{borrowerInfo.name}</strong> · Status: <span style={{ color: '#047857', fontWeight: 600 }}>{borrowerInfo.creditStatus || 'ACTIVE'}</span>
                    {borrowerInfo.creditScore != null ? ` · Score: ${borrowerInfo.creditScore}` : ''}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label="ELIGIBLE"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
              />
            </Box>
          )
        ) : null}

        {/* Amount Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <AttachMoney sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Amount Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {amountFields.map((field) => {
                const isAmountField = ['amount', 'outstanding', 'emiAmount'].includes(field.name);
                const InputComponent = isAmountField ? AmountInput : TextField;
                
                return (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <InputComponent
                      label={field.label}
                      name={field.name}
                      fullWidth
                      value={formData[field.name as keyof typeof formData] || ''}
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      helperText={
                        field.name === 'outstanding'
                          ? (outstandingManuallyEdited ? 'Custom outstanding set' : 'Auto-populated from Principal Amount')
                          : undefined
                      }
                      InputProps={{
                        inputProps: { min: 0, step: field.name === "interestRate" ? 0.01 : 1 }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Date Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'secondary.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <CalendarToday sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Date Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {dateFields.map((field) => (
                <Grid item xs={12} sm={4} key={field.name}>
                  <TextField
                    label={field.label}
                    name={field.name}
                    type="date"
                    fullWidth
                    value={formData[field.name as keyof typeof formData] || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Configuration Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'success.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <Settings sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Debit type"
                  name="type"
                  fullWidth
                  value={formData.type || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {Type.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Duration type"
                  name="durationType"
                  fullWidth
                  value={formData.durationType || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {durationTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Status"
                  name="status"
                  fullWidth
                  value={formData.status || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {statuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Chip 
                        label={option}
                        size="small"
                        color={
                          option === 'ACTIVE' ? 'success' :
                          option === 'PENDING' ? 'warning' :
                          option === 'CLOSED' ? 'info' : 'error'
                        }
                        sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
                      />
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Interest Projection - Only show when type is INTEREST */}
        {formData.type === "INTEREST" && formData.amount && formData.interestRate && formData.startDate && formData.endDate && (
          <Card elevation={2} sx={{ bgcolor: alpha(theme.palette.info.main, 0.06), border: '1px solid', borderColor: 'info.main' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="info.dark" gutterBottom fontWeight="bold">
                Interest Projection
              </Typography>
              {(() => {
                const principal = Number(formData.amount || 0);
                const rate = Number(formData.interestRate || 0);
                const startDate = new Date(formData.startDate);
                const endDate = new Date(formData.endDate);
                const daysDiff = Math.max(0, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
                const monthsDiff = Math.ceil(daysDiff / 30);
                const yearsDiff = daysDiff / 365;

                const monthlyInterest = (principal / 100) * rate;

                let calculatedInterest = 0;
                let periodLabel = '';
                let numberOfMonths = 0;

                if (formData.durationType === "Days") {
                  numberOfMonths = daysDiff / 30;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${daysDiff} days (≈ ${numberOfMonths.toFixed(1)} months)`;
                } else if (formData.durationType === "Weeks") {
                  const weeks = Math.ceil(daysDiff / 7);
                  numberOfMonths = weeks / 4.33;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${weeks} weeks (≈ ${numberOfMonths.toFixed(1)} months)`;
                } else if (formData.durationType === "Months") {
                  numberOfMonths = monthsDiff;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${monthsDiff} months`;
                } else if (formData.durationType === "Years") {
                  numberOfMonths = yearsDiff * 12;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${yearsDiff.toFixed(2)} years (${Math.round(numberOfMonths)} months)`;
                }

                const totalAmount = principal + calculatedInterest;

                return (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Period: <strong>{periodLabel}</strong>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Principal Amount: <strong>₹{principal.toLocaleString('en-IN')}</strong>
                        </Typography>
                        <Typography variant="body2" color="success.main" gutterBottom fontWeight="bold">
                          Interest Rate: <strong>{rate}% per month</strong>
                        </Typography>
                        <Typography variant="subtitle1" color="warning.dark" gutterBottom fontWeight="bold" sx={{ mt: 0.5 }}>
                          Monthly Interest: ₹{monthlyInterest.toFixed(2).toLocaleString()}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" color="error.main" fontWeight="bold">
                          Total Payable: ₹{totalAmount.toFixed(2).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Summary Card */}
        <Card elevation={1} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
              Summary
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Principal: <strong>₹{Number(formData.amount || 0).toLocaleString('en-IN')}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Outstanding: <strong>₹{Number(formData.outstanding || 0).toLocaleString('en-IN')}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Interest: <strong>{formData.interestRate || '0'}%</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  EMI: <strong>₹{Number(formData.emiAmount || 0).toLocaleString('en-IN')}</strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  ), [formData, handleChange, loader, borrowerInfo, isCustomerBlocked, loadingBorrowerCheck, outstandingManuallyEdited, theme]);

  return (
    <>
      {isMobile ? (
        <Dialog
          fullScreen
          open={open}
          onClose={handleDebitModelClose}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}>
            <Box display="flex" alignItems="center">
              <AttachMoney sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Add Outstanding Amount
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={handleDebitModelClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 2 }}>
            {FormContent}
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
            <Button 
              variant="outlined" 
              color="warning" 
              onClick={handleClear}
              startIcon={<Clear />}
              fullWidth
            >
              Clear
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={handleSubmit}
              startIcon={<CheckCircle />}
              fullWidth
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Modal open={open} onClose={handleDebitModelClose}>
          <Box sx={modalStyle}>
            <BackdropComponent visible={loader} />
            <ToastContainer />
            
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 1, fontSize: '1.4rem' }} />
                <Typography variant="h6" fontWeight="bold">
                  Add Outstanding Amount
                </Typography>
              </Box>
              <IconButton color="inherit" onClick={handleDebitModelClose}>
                <Close />
              </IconButton>
            </Box>
            
            {/* Content */}
            <Box sx={{ p: 2.5, flexGrow: 1, overflow: 'auto' }}>
              {FormContent}
            </Box>
            
            {/* Footer */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50',
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between'
            }}>
              <Button 
                variant="outlined" 
                color="warning" 
                onClick={handleClear}
                startIcon={<Clear />}
                sx={{ minWidth: 120 }}
              >
                Clear Form
              </Button>
              <Button 
                variant="contained" 
                color="success" 
                onClick={handleSubmit}
                startIcon={<CheckCircle />}
                sx={{ minWidth: 120, fontWeight: 700 }}
              >
                Submit Debit
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default DebitCaptureModal;
